<template>
  <v-container fluid class="py-0 px-2">
    <v-text-field
      v-model="search"
      background-color="colorSearchSettings"
      clearable
      :append-icon="mdiMagnify"
      :label="$t('label.search')"
      hide-details
    ></v-text-field>
    <div style="min-height: 1px"></div>
    <v-data-table
      :headers="headers"
      :items="tableData"
      item-key="id"
      :height="$store.state.windowSize.y - 150"
      :search="search"
      fixed-header
      must-sort
      disable-pagination
      hide-default-footer
      class="tableCursor"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.id }}</td>
          <td>{{ item.name }}</td>
          <td>{{ item.note }}</td>
          <td v-if="$store.state.auth.userSettings.accAreas.update" class="px-2 tableSelectCursor" @click="prepareEdit(item)">
            <v-icon>{{ mdiPen }}</v-icon>
          </td>
          <td v-else></td>
        </tr>
      </template>
    </v-data-table>
    <v-row>
      <v-col cols="3">
        <v-btn v-if="$store.state.auth.userSettings.accAreas.write" block color="colorNew" @click="dataNew">
          {{ $t('label.addNew') }}
        </v-btn>
      </v-col>
    </v-row>
    <!----------DIALOGS------------------------------>
    <v-dialog v-model="dialogEdit" persistent width="1020px">
      <v-card>
        <v-card-title class="headline colorHeadlineStandard" primary-title>
          {{ $tc('label.area') }}
          <v-spacer />
          <v-icon large @click="dialogEdit = false">{{ mdiClose }}</v-icon>
        </v-card-title>
        <v-card-text class="pa-3">
          <v-form v-model="validForm" ref="areaForm">
            <v-row>
              <v-col>
                <v-text-field
                  :label="$t('label.itemName')"
                  v-model="area.name"
                  :rules="[ruleRequired]"
                  maxlength="128"
                  required
                  hide-details
                  dense
                  autofocus
                  :prepend-inner-icon="mdiHomeCity"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-select
                  v-model="area.controlleditems"
                  :label="$tc('label.itemForInspection', 2)"
                  :items="listItems"
                  item-text="item"
                  item-value="id"
                  :menu-props="{ minWidth: '500' }"
                  :return-object="false"
                  multiple
                  chips
                  deletable-chips
                  append-icon="$dropdown"
                  hide-details
                  dense
                  :prepend-inner-icon="mdiBagPersonal"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="4">
                <v-select
                  v-model="area.report"
                  :label="$tc('label.printTemplate')"
                  :menu-props="{ minWidth: '500' }"
                  :items="listReports"
                  item-text="name"
                  item-value="id"
                  hide-details
                  dense
                  clearable
                  :prepend-inner-icon="mdiPrinter"
                />
              </v-col>
              <v-col cols="4">
                <v-autocomplete
                  v-if="$store.state.auth.settings.planVisits"
                  v-model="area.timezone"
                  :items="listTimezones"
                  item-text="text"
                  item-value="value"
                  :label="$t('label.timezone')"
                  hide-details
                  dense
                />
              </v-col>
              <v-col cols="4">
                <v-checkbox v-model="area.medicalCertificate" class="py-0 my-0" :label="$t('label.medicalCertificate')" hide-details />
              </v-col>
            </v-row>

            <v-row>
              <v-col v-if="$store.state.auth.settings.planVisits" cols="6">
                <v-row>
                  <v-col cols="6" class="py-0">
                    <v-checkbox class="my-0" :label="$t('label.useInPlanning')" v-model="area.planning" hide-details />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6" class="py-0">
                    <v-checkbox
                      class="my-0"
                      :label="$t('label.strictMode')"
                      v-model="area.planningStrict"
                      v-bind:disabled="!area.planning"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6" class="py-0">
                    <v-checkbox
                      class="my-0"
                      :label="$t('label.autoConfirm')"
                      v-model="area.planningAutopair"
                      v-bind:disabled="!area.planningStrict || !area.planning"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="3">
                    <v-text-field
                      :label="$t('label.toleranceBefore')"
                      type="number"
                      v-model.number="area.toleranceBefore"
                      v-bind:disabled="!area.planningAutopair"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      :label="$t('label.toleranceAfter')"
                      type="number"
                      v-model.number="area.toleranceAfter"
                      v-bind:disabled="!area.planningAutopair"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea
                      :label="$t('label.note')"
                      v-model="area.note"
                      name="input-7-1"
                      maxlength="512"
                      no-resize
                      hide-details
                      dense
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6" v-if="$store.state.auth.settings.autoSaveVisitors">
                <v-row>
                  <v-col cols="3">
                    <v-select
                      v-model="selectedSigLanguage"
                      :items="signTermLangs"
                      :return-object="false"
                      item-text="name"
                      item-value="code"
                      :label="$t('label.language')"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-select
                      v-model="selectedDocument"
                      :label="$tc('label.assignedDocuments')"
                      :menu-props="{ minWidth: '500' }"
                      :return-object="false"
                      :items="listDocuments"
                      item-text="name"
                      item-value="id"
                      hide-details
                      dense
                    />
                  </v-col>
                  <v-col cols="3">
                    <v-btn @click="appendDocument">{{ $t('btn.add') }}</v-btn>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6" class="py-0">
                    <b> {{ $tc('label.printTemplate') }} </b>
                  </v-col>
                  <v-col cols="1" class="pa-0">
                    <v-icon>{{ mdiTranslate }}</v-icon>
                  </v-col>
                  <v-col cols="4" class="py-0">
                    <b> {{ $tc('label.action') }} </b>
                  </v-col>
                  <v-col cols="1" class="py-0"><b> </b></v-col>
                </v-row>
                <v-row>
                  <v-list height="190px" width="100%" elevation="0" style="overflow: auto; overflow-x: hidden; text-overflow: hidden">
                    <v-list-item v-for="item in area.documents" :key="item.document" dense class="pl-0">
                      <v-col class="mt-2 py-0" cols="6" style="overflow: hidden; white-space: nowrap">
                        <h3>{{ listDocuments.find((x) => x.id === item.document).name }}</h3>
                      </v-col>
                      <v-col class="py-0 pt-2" cols="1">
                        <span>
                          <b>{{ item.language }}</b>
                        </span>
                      </v-col>
                      <v-col class="py-0" cols="4">
                        <v-select
                          v-model="item.action"
                          :items="listDocumentActions"
                          item-text="text"
                          item-value="value"
                          hide-details
                          dense
                          style="width: 110px"
                        />
                      </v-col>
                      <v-spacer />
                      <v-col class="pa-0" cols="1">
                        <v-btn icon>
                          <v-icon color="red" @click="((selectedSigDocument = item), (dialogDeleteSigDocument = true))">
                            {{ mdiDelete }}
                          </v-icon>
                        </v-btn>
                      </v-col>
                    </v-list-item>
                  </v-list>
                </v-row>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn v-if="area.id && $store.state.auth.userSettings.accAreas.delete" color="colorDelete" @click="dialogDelete = true">
            {{ $t('btn.delete') }}
          </v-btn>
          <v-spacer />
          <v-btn color="colorSave" @click="dataSave">{{ $t('btn.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}: ${area.name}?`"
      :openDialog.sync="dialogDelete"
      v-on:confirm="dataDelete"
    />
    <DialogConfirmDelete
      :header="$t('label.delete')"
      :body="`${$t('msg.confirmDelete')}?`"
      :openDialog.sync="dialogDeleteSigDocument"
      v-on:confirm="area.documents.splice(area.documents.indexOf(selectedSigDocument), 1)"
    />
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import DialogConfirmDelete from '@/components/DialogConfirmDelete.vue';

import {
  mdiMagnify,
  mdiPen,
  mdiClose,
  mdiHomeCity,
  mdiBagPersonal,
  mdiPrinter,
  mdiDelete,
  mdiTranslate,
  mdiMonitorDashboard,
} from '@mdi/js';

export default Vue.extend({
  name: 'SettingsAreas',
  components: { DialogConfirmDelete },
  data: () => ({
    dialogEdit: false,
    dialogDelete: false,
    dialogDeleteSigDocument: false,
    validForm: false,
    tableData: [],
    listItems: [],
    listReports: [], // Report/visitorCard for printing
    listDocuments: [{ id: 0, name: '' }], // Documents for signature or confirmation
    listAvailableWorkplaces: [], // Workplaces for area
    signTermLangs: [{ name: '', code: '' }],
    selectedDocument: 0,
    selectedSigLanguage: '',
    selectedSigDocument: 0,
    listTimezones: [],
    search: '',
    area: {
      id: 0,
      name: null,
      note: null,
      report: 0,
      controlleditems: [],
      planning: true,
      planningStrict: false,
      planningAutopair: false,
      toleranceBefore: 15,
      toleranceAfter: 15,
      medicalCertificate: false,
      timezone: '',
      documents: [],
    },
    mdiMagnify: mdiMagnify,
    mdiPen: mdiPen,
    mdiClose: mdiClose,
    mdiHomeCity: mdiHomeCity,
    mdiBagPersonal: mdiBagPersonal,
    mdiPrinter: mdiPrinter,
    mdiDelete: mdiDelete,
    mdiTranslate: mdiTranslate,
    mdiMonitorDashboard: mdiMonitorDashboard,
  }),
  computed: {
    ruleRequired() {
      return (v: any) => !!v || this.$i18n.t('msg.fieldIsMandatory');
    },
    headers: function () {
      return [
        { text: 'ID', value: 'id', width: '65px' },
        { text: this.$i18n.t('label.itemName'), value: 'name' },
        { text: this.$i18n.t('label.note'), value: 'note' },
        { sortable: false, width: '48px' },
      ];
    },
    listDocumentActions: function () {
      return [
        { text: this.$i18n.tc('label.notActive'), value: 0 },
        { text: this.$i18n.tc('label.confirm'), value: 1 },
        { text: this.$i18n.tc('label.sign'), value: 2 },
      ];
    },
  },
  mounted() {
    this.listItemsLoad();
    this.listReportsLoad();
    this.listTimezonesLoad();
    this.listSignTermLangsLoad();
    this.$nextTick(function () {
      this.dataLoad();
    });
  },
  methods: {
    dataLoad() {
      axios
        .get('/api/v1/area')
        .then((response) => {
          this.tableData = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    dataNew() {
      const defaults: any = this.$options;
      this.area = { ...defaults.data().area };
      this.dialogEdit = true;
      this.defaultTimezoneLoad();
      this.$nextTick(function () {
        const form: any = this.$refs.areaForm;
        form.validate();
      });
    },
    dataSave() {
      if (this.area.toleranceBefore + this.area.toleranceAfter < 5) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.errCountTolerance'));
        return;
      }
      const form: any = this.$refs.areaForm;
      if (form.validate() !== true) {
        this.$store.commit('globalMessage', this.$i18n.t('msg.formNotValid'));
      } else {
        if (this.area.id === 0) {
          axios
            .post('/api/v1/area', this.area)
            .then((response) => {
              if (response.data.status === 'recordExists') {
                this.$store.commit('globalMessage', this.$i18n.t('msg.itemNameExists'));
              } else {
                this.dataLoad();
                this.dialogEdit = false;
              }
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        } else {
          axios
            .put('/api/v1/area', this.area)
            .then((response) => {
              if (response.data.status === 'recordExists') {
                this.$store.commit('globalMessage', this.$i18n.t('msg.itemNameExists'));
              } else {
                this.dataLoad();
                this.dialogEdit = false;
              }
            })
            .catch((err) => {
              this.$store.dispatch('catchErrHandler', err);
            });
        }
      }
    },
    dataDelete() {
      axios
        .delete(`/api/v1/area/${this.area.id}`)
        .then((response) => {
          if (response.data.message === 'errorFK') {
            this.$store.commit('globalMessage', this.$i18n.t('msg.errAreaCannotBeDeleted'));
            this.dialogDelete = false;
            return;
          }
          this.dataLoad();
          this.dialogDelete = false;
          this.dialogEdit = false;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
          this.dialogDelete = false;
          this.dialogEdit = false;
        });
    },
    prepareEdit(item: any) {
      this.area = { ...item };
      this.dialogEdit = true;
    },
    listItemsLoad() {
      axios
        .get('/api/v1/item')
        .then((response) => {
          this.listItems = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    listReportsLoad() {
      axios
        .get('/api/v1/template')
        .then((response) => {
          this.listReports = [];
          for (const item of response.data) {
            item.type === 'visitorCard' ? this.listReports.push(item) : null;
            item.type === 'areaDocument' ? this.listDocuments.push(item) : null;
          }
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    listSignTermLangsLoad() {
      axios
        .get('/api/v1/signtermlangs')
        .then((response) => {
          this.signTermLangs = [];
          let termLang = {} as any;
          for (termLang of Object.entries(response.data)) {
            let lang = { code: termLang[1].langID, name: termLang[1].langName };
            this.signTermLangs.push(lang);
          }
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    listTimezonesLoad() {
      axios
        .get('/api/v1/timezones')
        .then((response) => {
          this.listTimezones = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },

    defaultTimezoneLoad() {
      axios
        .get('/api/v1/timezones/default')
        .then((response) => {
          this.area.timezone = response.data;
        })
        .catch((err) => {
          this.$store.dispatch('catchErrHandler', err);
        });
    },
    appendDocument() {
      if (this.selectedSigLanguage === '') {
        this.$store.commit('globalMessage', this.$i18n.t('msg.languageRequired'));
        return;
      }
      this.area.documents === null ? (this.area.documents = []) : null;
      if (this.selectedDocument !== 0 && !this.area.documents.find((x) => x.document === this.selectedDocument)) {
        this.area.documents.push({
          document: this.selectedDocument,
          action: 0,
          language: this.selectedSigLanguage,
        });
      }
    },
  },
});
</script>
